import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader, Dot } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["green", "amber", "red"];

export default function Ecosystem() {
  const { effectiveRole } = useAuth();
  const canWrite = effectiveRole !== "viewer";
  const [nodes, setNodes] = useState([]);
  const [links, setLinks] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [n, l] = await Promise.all([api.get("/eco-nodes"), api.get("/eco-links")]);
    setNodes(n.data); setLinks(l.data);
  };
  useEffect(() => { load(); }, []);

  const nodeById = (id) => nodes.find((n) => n.id === id);

  const save = async () => {
    setSaving(true);
    try {
      if (form.id) await api.put(`/eco-nodes/${form.id}`, form);
      else await api.post("/eco-nodes", { ...form, x: 50, y: 50 });
      toast.success("Node saved"); setOpen(false); setTimeout(load, 250);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="ecosystem-page">
      <PageHeader eyebrow="Vision" title="Youngness Ecosystem Builder"
        subtitle="Map clinics, institute, products, partners, community and the patient app — and how they connect."
        action={<Button onClick={() => { setForm({ name: "", type: "channel", description: "", owner: "", status: "amber", x: 50, y: 50 }); setOpen(true); }} data-testid="add-node-button" style={{ display: canWrite ? undefined : "none" }}>Add Node</Button>} />

      <Card className="relative overflow-hidden mb-6" style={{ height: 480 }} data-testid="ecosystem-map">
        <svg className="absolute inset-0 w-full h-full pointer-events-none">
          {links.map((l, i) => {
            const a = nodeById(l.from), b = nodeById(l.to);
            if (!a || !b) return null;
            return <line key={i} x1={`${a.x}%`} y1={`${a.y}%`} x2={`${b.x}%`} y2={`${b.y}%`} stroke="hsl(var(--border))" strokeWidth="2" />;
          })}
        </svg>
        {nodes.map((n) => (
          <button key={n.id} onClick={() => { setForm(n); setOpen(true); }} data-testid={`eco-node-${n.id}`}
            className="absolute -translate-x-1/2 -translate-y-1/2 bg-card border border-border rounded-xl px-3 py-2 shadow-sm hover:shadow-md hover:border-primary transition-all duration-200 text-left w-36"
            style={{ left: `${n.x}%`, top: `${n.y}%` }}>
            <div className="flex items-center gap-1.5"><Dot status={n.status} /><p className="text-xs font-semibold truncate">{n.name}</p></div>
            <p className="text-[10px] text-muted-foreground truncate">{n.owner}</p>
          </button>
        ))}
      </Card>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {nodes.map((n) => (
          <Card key={n.id} className="p-4">
            <div className="flex items-center gap-2 mb-1"><Dot status={n.status} /><p className="font-semibold text-sm">{n.name}</p>
              <Badge variant="secondary" className="ml-auto text-[10px]">{n.type}</Badge></div>
            <p className="text-xs text-muted-foreground">{n.description}</p>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="node-dialog">
          <DialogHeader><DialogTitle>{form.id ? "Edit Node" : "Add Node"}</DialogTitle>
            <DialogDescription>Each node has an owner, status and description.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label className="text-xs">Name</Label><Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="node-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs">Type</Label><Input value={form.type || ""} onChange={(e) => setForm({ ...form, type: e.target.value })} /></div>
              <div className="space-y-1.5"><Label className="text-xs">Owner</Label><Input value={form.owner || ""} onChange={(e) => setForm({ ...form, owner: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}><SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-1.5"><Label className="text-xs">Description</Label><Textarea value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving || !form.name} data-testid="save-node-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
